<template>
  <div class="hero-slider" style="background-color: #021a70;">
    <transition name="fade" mode="out-in">
      <div
        :key="currentSlide"
        class="slide"
      >
        <div class="slide-content">
          <h1 class="text-3xl md:text-5xl font-bold mb-6 text-white">
            {{ slides[currentSlide].title }}
          </h1>
          <p class="text-lg md:text-xl mb-8 text-grisclairbleute max-w-3xl mx-auto">
            {{ slides[currentSlide].description }}
          </p>
          <div class="flex flex-col sm:flex-row gap-4 justify-center">
            <router-link to="/contact" class="text-white font-semibold py-3 px-6 rounded-lg transition duration-300 text-center" style="background-color: #29e8c6;">
              Demander un Devis
            </router-link>
            <router-link to="/services" class="text-white font-semibold py-3 px-6 rounded-lg transition duration-300 text-center" style="background-color: #a967ff;">
              Nos Services
            </router-link>
          </div>
        </div>
      </div>
    </transition>

    <!-- Slider Indicators -->
    <div class="slider-indicators">
      <button
        v-for="(slide, index) in slides"
        :key="index"
        @click="goToSlide(index)"
        :class="{ active: currentSlide === index }"
        class="indicator"
      ></button>
    </div>

    <!-- Navigation Arrows -->
    <button @click="prevSlide" class="nav-arrow prev hidden md:block">
      <ChevronLeft class="w-6 h-6" :stroke="'#fff'" />
    </button>
    <button @click="nextSlide" class="nav-arrow next hidden md:block">
      <ChevronRight class="w-6 h-6" :stroke="'#fff'" />
    </button>
  </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount } from 'vue'
import { ChevronLeft, ChevronRight } from 'lucide-vue-next'

const currentSlide = ref(0)
const timer = ref(null)

const slides = ref([
  {
    title: 'Le partenaire idéal pour votre système d\'information',
    description: "Tech's Partner est une entreprise spécialisée dans la conception, l'implémentation et l'administration de systèmes d'information."
  },
  {
    title: 'Transformation digitale en Afrique subsaharienne',
    description: 'Nous accompagnons les entreprises et institutions dans leur transformation numérique avec des solutions innovantes et sécurisées.'
  },
  {
    title: 'Expertise technique et accompagnement sur mesure',
    description: 'Notre consortium d\'experts certifiés vous accompagne dans tous vos projets IT : infrastructure, réseaux, développement et sécurité.'
  }
])

const nextSlide = () => {
  currentSlide.value = (currentSlide.value + 1) % slides.value.length
  resetAutoPlay()
}

const prevSlide = () => {
  currentSlide.value = (currentSlide.value - 1 + slides.value.length) % slides.value.length
  resetAutoPlay()
}

const goToSlide = (index) => {
  currentSlide.value = index
  resetAutoPlay()
}

const startAutoPlay = () => {
  timer.value = setInterval(() => {
    nextSlide()
  }, 5000)
}

const stopAutoPlay = () => {
  if (timer.value) {
    clearInterval(timer.value)
  }
}

const resetAutoPlay = () => {
  stopAutoPlay()
  startAutoPlay()
}

onMounted(() => {
  startAutoPlay()
})

onBeforeUnmount(() => {
  stopAutoPlay()
})
</script>

<style scoped>
.hero-slider {
  position: relative;
  width: 100%;
  min-height: 400px;
  background-color: #021a70 !important;
}

.slide {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.slide-content {
  text-align: center;
  padding: 2rem;
}

/* Fade transition */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.8s ease-in-out;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* Slider indicators */
.slider-indicators {
  position: absolute;
  bottom: 2rem;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 0.5rem;
}

.indicator {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.5);
  border: 2px solid white;
  cursor: pointer;
  transition: all 0.3s ease;
}

.indicator:hover {
  background: rgba(255, 255, 255, 0.8);
}

.indicator.active {
  background: white;
  width: 32px;
  border-radius: 6px;
}

/* Navigation arrows */
.nav-arrow {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.2);
  border: 2px solid rgba(255, 255, 255, 0.5);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  backdrop-filter: blur(4px);
}

.nav-arrow:hover {
  background: rgba(255, 255, 255, 0.4);
  transform: translateY(-50%) scale(1.1);
}

.nav-arrow.prev {
  left: 1rem;
}

.nav-arrow.next {
  right: 1rem;
}

@media (max-width: 768px) {
  .nav-arrow {
    width: 40px;
    height: 40px;
  }

  .nav-arrow.prev {
    left: 0.5rem;
  }

  .nav-arrow.next {
    right: 0.5rem;
  }

  .slider-indicators {
    bottom: 1.5rem;
  }
}
</style>
